import { MRT_ColumnDef } from 'material-react-table';
import { MenuItem } from '@mui/material';
import { beautifyEnum } from 'src/utils/utility';
import { formatDate } from 'src/utils/date';
import { CHARGE_CATEGORIES, LEG_TYPES } from '../../contractManager/rateCardCharges/constants';

/**
 * Common prefix columns that appear at the start of all charge tables
 * These columns are: Charge Name, Charge Category, Leg Type
 */
export const getCommonPrefixColumns = <T,>(): MRT_ColumnDef<T>[] => [
  {
    accessorKey: 'chargeName',
    header: 'Charge Name',
    Cell: ({ row }: any) => <span className='text-12 xl:text-14'>{row?.original?.chargeName ? beautifyEnum(row.original.chargeName).toUpperCase() : '-'}</span>,
    muiEditTextFieldProps: {
      type: 'text',
      required: true,
    },
  },
  {
    accessorKey: 'chargeCategory',
    header: 'Charge Category',
    Cell: ({ row }: any) => (
      <span className='text-12 xl:text-14'>{row?.original?.chargeCategory ? beautifyEnum(row.original.chargeCategory).toUpperCase() : '-'}</span>
    ),
    muiEditTextFieldProps: ({ row }: any) => ({
      select: true,
      required: true,
      children: CHARGE_CATEGORIES.map((category) => (
        <MenuItem key={category} value={category}>
          {beautifyEnum(category)}
        </MenuItem>
      )),
    }),
  },
  {
    accessorKey: 'legType',
    header: 'Leg Type',
    Cell: ({ row }: any) => <span className='text-12 xl:text-14'>{row?.original?.legType ? beautifyEnum(row.original.legType).toUpperCase() : '-'}</span>,
    muiTableHeadCellProps: {
      sx: {
        minWidth: '80px',
        borderBottom: '1px solid #DFDFDF !important',
        '& .Mui-TableHeadCell-Content-Wrapper': {
          width: '80px',
        },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        minWidth: '80px',
        borderBottom: '1px solid #DFDFDF !important',
      },
    },
    muiEditTextFieldProps: ({ row }: any) => ({
      select: true,
      required: true,
      children: LEG_TYPES.map((legType) => (
        <MenuItem key={legType} value={legType}>
          {beautifyEnum(legType)}
        </MenuItem>
      )),
    }),
  },
];

const BOOLEAN_OPTIONS = [
  { value: true, label: 'True' },
  { value: false, label: 'False' },
];

/**
 * Common suffix columns that appear at the end of all charge tables
 * These columns are: Accessorial, Approval, Outlay
 */
export const getCommonSuffixColumns = <T,>(): MRT_ColumnDef<T>[] => [
  {
    accessorKey: 'accessorial',
    header: 'Accessorial',
    Cell: ({ row }: any) => {
      if (row.original?.accessorial === undefined) return <span className='text-14 xl:text-16'>-</span>;
      return <span className='text-14 xl:text-16'>{row.original.accessorial ? 'True' : 'False'}</span>;
    },
    muiEditTextFieldProps: {
      select: true,
      children: BOOLEAN_OPTIONS.map((option) => (
        <MenuItem key={option.label} value={option.value as any}>
          {option.label}
        </MenuItem>
      )),
    },
  },
  {
    accessorKey: 'approval',
    header: 'Approval',
    Cell: ({ row }: any) => {
      if (row.original?.approval === undefined) return <span className='text-14 xl:text-16'>-</span>;
      return <span className='text-14 xl:text-16'>{row.original.approval ? 'True' : 'False'}</span>;
    },
    muiEditTextFieldProps: {
      select: true,
      children: BOOLEAN_OPTIONS.map((option) => (
        <MenuItem key={option.label} value={option.value as any}>
          {option.label}
        </MenuItem>
      )),
    },
  },
  {
    accessorKey: 'outlay',
    header: 'Outlay',
    Cell: ({ row }: any) => {
      if (row.original?.outlay === undefined) return <span className='text-14 xl:text-16'>-</span>;
      return <span className='text-14 xl:text-16'>{row.original.outlay ? 'True' : 'False'}</span>;
    },
    muiEditTextFieldProps: {
      select: true,
      children: BOOLEAN_OPTIONS.map((option) => (
        <MenuItem key={option.label} value={option.value as any}>
          {option.label}
        </MenuItem>
      )),
    },
  },
];

/**
 * Helper function to format date for display
 */
export const formatDateForDisplay = (dateString?: string): string => {
  if (!dateString) return '-';
  try {
    return formatDate(new Date(dateString));
  } catch {
    return '-';
  }
};
